"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ArrowRight, ShieldCheck, Truck, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const californiaCities = ["Agoura Hills, CA", "Alhambra, CA", "Anaheim, CA", "Anaheim Hills, CA", "Arcadia, CA", "Bakersfield, CA", "Beverly Hills, CA", "Big Bear Lake, CA", "Brea, CA", "Buellton, CA", "Burbank, CA", "Calabasas, CA", "Camarillo, CA", "Canyon Country, CA", "Canoga Park, CA", "Carpinteria, CA", "Chatsworth, CA", "Chino, CA", "Chino Hills, CA", "City of Commerce, CA", "City of Industry, CA", "Compton, CA", "Corona, CA", "Costa Mesa, CA", "Culver City, CA", "Dana Point, CA", "Diamond Bar, CA", "Downey, CA", "El Monte, CA", "Encino, CA", "Fillmore, CA", "Fountain Valley, CA", "Fullerton, CA", "Garden Grove, CA", "Glendale, CA", "Glendora, CA", "Goleta, CA", "Granada Hills, CA", "Hollywood, CA", "Huntington Beach, CA", "Irvine, CA", "Laguna Niguel, CA", "Lake Elsinore, CA", "Lancaster, CA", "Lompoc, CA", "Long Beach, CA", "Los Alamos, CA", "Los Angeles, CA", "Los Olivos, CA", "Malibu, CA", "Manhattan Beach, CA", "Marina del Rey, CA", "Menifee, CA", "Mission Viejo, CA", "Montecito, CA", "Monterey Park, CA", "Moorpark, CA", "Murrieta, CA", "Newhall, CA", "North Hollywood, CA", "Northridge, CA", "Ojai, CA", "Ontario, CA", "Orange, CA", "Oxnard, CA", "Pacific Palisades, CA", "Palmdale, CA", "Paramount, CA", "Pasadena, CA", "Porter Ranch, CA", "Poway, CA", "Reseda, CA", "Riverside, CA", "San Dimas, CA", "San Fernando, CA", "San Gabriel, CA", "San Pedro, CA", "Santa Ana, CA", "Santa Barbara, CA", "Santa Clarita, CA", "Santa Fe Springs, CA", "Santa Maria, CA", "Santa Monica, CA", "Santa Paula, CA", "Santa Ynez, CA", "Saugus, CA", "Sherman Oaks, CA", "Simi Valley, CA", "Solvang, CA", "Studio City, CA", "Sun Valley, CA", "Sylmar, CA", "Tarzana, CA", "Temecula, CA", "Temescal Valley, CA", "Thousand Oaks, CA", "Topanga, CA", "Torrance, CA", "Tustin, CA", "Valencia, CA", "Van Nuys, CA", "Venice, CA", "Ventura, CA", "Walnut, CA", "West Covina, CA", "West Hills, CA", "West Hollywood, CA", "Westlake Village, CA", "Whittier, CA", "Wildomar, CA", "Woodland Hills, CA", "Yorba Linda, CA"];
const nevadaCities = ["Las Vegas, NV", "North Las Vegas, NV", "Henderson, NV", "Boulder City, NV", "Paradise, NV", "Spring Valley, NV", "Enterprise, NV", "Sunrise Manor, NV", "Whitney, NV", "Winchester, NV", "Summerlin, NV", "Centennial Hills, NV"];
export default function Locations() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="serviceareaslocationslist" data-nav="light" className="bg-background text-foreground border-b border-border py-14 md:py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.24,
        ease: "easeOut"
      }} className="space-y-12">
          {/* Header & Intro */}
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs">
                <Truck className="size-3.5 text-primary mr-1" />
                Delivery Directory
              </Badge>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight text-foreground">
              Communities we serve
            </h2>

            <p className="text-base md:text-lg text-foreground/80 leading-relaxed">
              The communities below represent Fridge Fleet's general service region across Southern California and the Las Vegas area. Coverage, trailer availability, delivery requirements, site access, and scheduling are confirmed individually for each location and rental.
            </p>
          </div>

          <Separator />

          {/* Region 1: Southern California */}
          <div id="southern-california" className="space-y-6 scroll-mt-24">
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <MapPin className="size-5 text-primary shrink-0" />
                <h3 className="text-2xl font-bold font-default text-foreground">
                  Southern California
                </h3>
              </div>
              <span className="text-sm font-medium text-foreground/70">
                Listed communities
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5 text-sm">
              {californiaCities.map((city, i) => <div key={city} data-index={i} className="flex items-center gap-2 py-1 text-foreground/80 hover:text-foreground transition-colors">
                  <span className="size-1.5 rounded-full bg-primary/60 shrink-0" />
                  {city === "Los Angeles, CA" ? <Link to="/LosAngelesRefrigeratedTrailerRentalPage" className="truncate">
                      {city}
                    </Link> : <span className="truncate">{city}</span>}
                </div>)}
            </div>
          </div>

          <Separator />

          {/* Region 2: Las Vegas Area */}
          <div id="las-vegas-area" className="space-y-6 scroll-mt-24">
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <MapPin className="size-5 text-primary shrink-0" />
                <h3 className="text-2xl font-bold font-default text-foreground">
                  Las Vegas Area
                </h3>
              </div>
              <span className="text-sm font-medium text-foreground/70">
                Listed communities
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2.5 text-sm">
              {nevadaCities.map((city, i) => <div key={city} data-index={i} className="flex items-center gap-2 py-1 text-foreground/80 hover:text-foreground transition-colors">
                  <span className="size-1.5 rounded-full bg-primary/60 shrink-0" />
                  {city === "Las Vegas, NV" ? <Link to="/LasVegasRefrigeratedTrailerRentalPage" className="truncate">
                      {city}
                    </Link> : <span className="truncate">{city}</span>}
                </div>)}
            </div>

            {/* Informational Note */}
            <div className="rounded-lg border border-border bg-muted/40 p-4 text-xs sm:text-sm text-foreground/75 leading-relaxed flex items-start gap-2.5">
              <Info className="size-4 text-primary shrink-0 mt-0.5" />
              <p>
                The communities shown represent Fridge Fleet's general service region. Listing a community does not guarantee trailer availability or delivery. Coverage, scheduling, site access, and trailer-specific requirements are confirmed with each quote.
              </p>
            </div>
          </div>

          {/* Call to action panel */}
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
            <div className="space-y-1.5 max-w-2xl">
              <div className="flex items-center gap-2 text-foreground font-semibold text-base sm:text-lg">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <span>Need service outside a listed community?</span>
              </div>
              <p className="text-sm text-foreground/70">
                Submit your delivery address or ZIP code with your quote request so coverage, trailer availability, and delivery requirements can be reviewed individually.
              </p>
            </div>

            <Button asChild size="lg" className="w-full sm:w-auto shrink-0 font-medium">
              <Link to="/ContactRequestAQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}